<template>
  <div>
    <MessagesNav
      :unreadMessageCount="unreadMessageCount"
      :unreadInformationCount="unreadInformationCount"
    ></MessagesNav>
    <div class="container">
      <PullToRefresh
        @pull="pullToFetchInformationTargets"
        :isShowingPullToRefreshLoading="isShowingPullToRefreshLoading"
      >
        <ul v-if="informationTargets.length > 0">
          <li v-for="target of informationTargets" :key="target.id">
            <RouterLink
              :to="{
                name: 'InformationDetail',
                params: { informationId: target.information.id }
              }"
            >
              <div
                class="information bg-white border-bottom border-outline-color p-3"
                data-testid="information"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div class="flex-grow-1">
                    <p class="text-size-14 text-weight-500">
                      {{ target.information.title }}
                    </p>
                  </div>

                  <div
                    class="notification flex-shrink-0 d-flex flex-column justify-content-between align-items-end"
                  >
                    <p class="text-mild text-size-12 mb-1" data-testid="date">
                      {{ toDisplayTime(target.information.updatedAt) }}
                    </p>
                    <AppBadge
                      variant="red"
                      data-testid="unread-badge"
                      v-if="target.isRead === false"
                      >未読
                    </AppBadge>
                  </div>

                  <div class="arrow flex-shrink-0">
                    <FeatherIcon
                      icon="chevron-right"
                      size="1x"
                      class="text-gray-05 ml-2"
                    />
                  </div>
                </div>
              </div>
            </RouterLink>
          </li>
        </ul>
        <p
          v-else
          class="p-3 m-3 bg-white text-size-14 text-center"
          data-testid="information-empty"
        >
          まだお知らせはありません
        </p>
      </PullToRefresh>
    </div>
    <AppFooter />
  </div>
</template>
<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import { informationService } from "@/app/services/InformationService";
import { APP_SERVICE_NAME_KANA } from "@/const/app";
import { parseISO } from "@/utils/date";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import MessagesNav from "@/components/organisms/messages/MessagesNav.vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import { messageStore } from "@/store/message";
import PullToRefresh from "@/components/molecules/other/PullToRefresh.vue";
import { relativeTime } from "@/utils/relativeTime";
import AppBadge from "@/components/atoms/other/AppBadge.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { InformationTarget } from "@/types/message/InformationTarget";

const FETCH_MESSAGE_COUNT = 50;

@Component({
  components: {
    MessagesNav,
    AppFooter,
    PullToRefresh,
    AppBadge,
    FeatherIcon
  }
})
export default class Information extends Vue {
  informationTargets: InformationTarget[] = [];
  hasNext = false;
  isShowingPullToRefreshLoading = false;

  async mounted() {
    try {
      this.$loading.show();
      await this.fetchInformationTargets();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  onClickBack() {
    this.$router.back();
  }

  async fetchInformationTargets() {
    this.isShowingPullToRefreshLoading = true;
    const { next, results } = await informationService.fetchInformationTargets({
      limit: FETCH_MESSAGE_COUNT,
      offset: this.informationTargets.length
    });
    if (this.informationTargets.length === 0) {
      this.informationTargets = results;
    } else {
      this.informationTargets = this.informationTargets.concat(results);
    }
    this.hasNext = !!next;
    this.isShowingPullToRefreshLoading = false;
  }

  async pullToFetchInformationTargets() {
    try {
      this.isShowingPullToRefreshLoading = true;
      await this.fetchInformationTargets();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.isShowingPullToRefreshLoading = false;
    }
  }

  get title() {
    return `${APP_SERVICE_NAME_KANA}運営事務局`;
  }

  toDisplayTime(isoDate: string) {
    const date = parseISO(isoDate);
    if (!date) return "";
    return relativeTime(date);
  }

  get unreadMessageCount() {
    return messageStore.unreadMessageCount;
  }

  get unreadInformationCount() {
    return messageStore.unreadInformationCount;
  }
}
</script>
<style lang="scss" scoped>
.container {
  position: relative;
  padding: 0;

  .information {
    min-height: 72px;

    .notification {
      width: 60px;
    }

    .arrow {
      width: 30px;
    }
  }
}
</style>
